import React, { useEffect, useMemo, useState } from 'react';
import Input from '../../../components/ui/Input';
import Textarea from '../../../components/ui/Textarea';
import FormSection from '../../../components/form/FormSection';
import TextFieldActions from '../../../components/textVersions/TextFieldActions';
import { improveToneOfVoice, improveSubAgentInstructions, improveMcpUsageInstructions, triageToneOfVoice, triageSubAgentInstructions, triageMcpUsageInstructions, mergeImproveConsiderations } from '../../../api/onboarding';
import { createTextFieldVersion } from '../../../api/textFieldVersions';
import { listKnowledgeBases } from '../../../api/knowledgeBases';
import { listMcpServers } from '../../../api/mcpServers';
import { listLlmProfiles } from '../../../api/llmProfiles';
import { useTextVersionStack } from '../../../hooks/useTextVersionStack';
import { buildTextFieldStackKey, isPersistedUuid } from '../../../utils/resourceIds';
import {
  createEmptySubAgent,
  getExecutionPhase,
  createDefaultProcessingFeedbackForm,
  buildProcessingFeedbackConfiguration,
  buildBehaviorExecutionConfiguration,
  buildEscalationCooldownConfiguration,
  buildMessagePolishConfiguration,
  buildResponseDepthConfiguration,
  buildMessageFormatConfiguration,
  buildLongRunningAnalysisConfiguration,
  createDefaultLongRunningAnalysisForm,
  DEFAULT_SESSION_CLOSE_POLICY,
  DEFAULT_ESCALATION_COOLDOWN_HOURS,
  DEFAULT_INTERACTIVE_LIST_BUTTON_LABEL,
  getCompletionWebhookConfigError,
  isSubAgentIncomplete,
} from '../constants';
import AgentAdvancedSettings from '../components/AgentAdvancedSettings';
import ImproveTextModal from '../components/ImproveTextModal';
import ImprovePreviewModal from '../components/ImprovePreviewModal';
import SubAgentList from '../components/SubAgentList';
import WizardShell from '../components/WizardShell';
import StepActions from '../components/StepActions';

function mergeWizardKnowledgeBase(knowledgeBases, wizardKnowledgeBase) {
  if (!wizardKnowledgeBase?.id || wizardKnowledgeBase.status === 'error') {
    return knowledgeBases;
  }

  if (knowledgeBases.some((item) => item.id === wizardKnowledgeBase.id)) {
    return knowledgeBases;
  }

  return [
    {
      id: wizardKnowledgeBase.id,
      name: wizardKnowledgeBase.name || 'Base criada no wizard',
      status: wizardKnowledgeBase.status || 'ingesting',
      source_type: wizardKnowledgeBase.sourceType || 'url',
      source_ref: wizardKnowledgeBase.sourceRef || null,
    },
    ...knowledgeBases,
  ];
}

function filterSelectableKnowledgeBases(knowledgeBases) {
  return knowledgeBases.filter((kb) => kb.status !== 'error');
}

function AgentSetup({
  mode = 'create',
  stepNumber,
  totalSteps,
  hasKnowledge,
  wizardKnowledgeBase = null,
  initialValues = null,
  isLoadingInitial = false,
  persistedAgentId = null,
  onBack,
  onSubmit,
  isSubmitting,
  submitError,
}) {
  const isEditMode = mode === 'edit';
  const textVersionStack = useTextVersionStack();

  const [agentName, setAgentName] = useState(initialValues?.agentName || '');
  const [toneOfVoice, setToneOfVoice] = useState(initialValues?.toneOfVoice || '');
  const [subAgents, setSubAgents] = useState(initialValues?.subAgents || []);
  const [llmModelLines, setLlmModelLines] = useState([]);
  const [llmEfforts, setLlmEfforts] = useState([]);
  const [llmModelLineId, setLlmModelLineId] = useState(initialValues?.llmModelLineId || '');
  const [llmEffortId, setLlmEffortId] = useState(initialValues?.llmEffortId || '');
  const [isLoadingLlmCatalog, setIsLoadingLlmCatalog] = useState(true);
  const [llmCatalogError, setLlmCatalogError] = useState('');
  const [sessionTimeoutHours, setSessionTimeoutHours] = useState(
    initialValues?.sessionTimeoutHours || '4'
  );
  const [sessionClosePolicy, setSessionClosePolicy] = useState(
    initialValues?.sessionClosePolicy || DEFAULT_SESSION_CLOSE_POLICY
  );
  const [escalationCooldownHours, setEscalationCooldownHours] = useState(
    initialValues?.escalationCooldownHours ?? DEFAULT_ESCALATION_COOLDOWN_HOURS
  );
  const [knowledgeRelevanceMode, setKnowledgeRelevanceMode] = useState(
    initialValues?.knowledgeRelevanceMode || 'high'
  );
  const [processingFeedback, setProcessingFeedback] = useState(
    initialValues?.processingFeedback || createDefaultProcessingFeedbackForm()
  );
  const [requireRegisteredContact, setRequireRegisteredContact] = useState(
    initialValues?.requireRegisteredContact || false
  );
  const [shareCrossAgentContext, setShareCrossAgentContext] = useState(
    initialValues?.shareCrossAgentContext || false
  );
  const [behaviorExecutionSelective, setBehaviorExecutionSelective] = useState(
    Boolean(initialValues?.behaviorExecutionSelective)
  );
  const [knowledgeFileButtonEnabled, setKnowledgeFileButtonEnabled] = useState(
    initialValues?.knowledgeFileButtonEnabled !== false
  );
  const [interactiveOptionsEnabled, setInteractiveOptionsEnabled] = useState(
    Boolean(initialValues?.interactiveOptionsEnabled)
  );
  const [interactiveListButtonLabel, setInteractiveListButtonLabel] = useState(
    initialValues?.interactiveListButtonLabel || ''
  );
  const [visualOutputEnabled, setVisualOutputEnabled] = useState(
    Boolean(initialValues?.visualOutputEnabled)
  );
  const [visualOutputFormats, setVisualOutputFormats] = useState(
    initialValues?.visualOutputFormats || ['png', 'jpg', 'pdf']
  );
  const [visualOutputStyleInstructions, setVisualOutputStyleInstructions] = useState(
    initialValues?.visualOutputStyleInstructions || ''
  );
  const [visualOutputAssets, setVisualOutputAssets] = useState(
    initialValues?.visualOutputAssets || {}
  );
  const [messagePolishMode, setMessagePolishMode] = useState(
    initialValues?.messagePolishMode || 'standard'
  );
  const [responseDepth, setResponseDepth] = useState(
    initialValues?.responseDepth || 'chat'
  );
  const [messageFormat, setMessageFormat] = useState(
    initialValues?.messageFormat || 'simple'
  );
  const [longRunningAnalysis, setLongRunningAnalysis] = useState(
    initialValues?.longRunningAnalysis || createDefaultLongRunningAnalysisForm()
  );
  const [validationError, setValidationError] = useState('');
  const [isImprovingTone, setIsImprovingTone] = useState(false);
  const [improvingSubAgentId, setImprovingSubAgentId] = useState(null);
  const [toneImproveError, setToneImproveError] = useState('');
  const [improveModalKind, setImproveModalKind] = useState(null);
  const [improveModalPhase, setImproveModalPhase] = useState('form');
  const [triageQuestions, setTriageQuestions] = useState([]);
  const [pendingImproveConsiderations, setPendingImproveConsiderations] = useState('');
  const [pendingImproveSubAgent, setPendingImproveSubAgent] = useState(null);
  const [pendingImproveMcpBinding, setPendingImproveMcpBinding] = useState(null);
  const [improvingMcpBindingId, setImprovingMcpBindingId] = useState(null);
  const [improveModalError, setImproveModalError] = useState('');
  const [isImproveModalSubmitting, setIsImproveModalSubmitting] = useState(false);
  const [improvePreview, setImprovePreview] = useState(null);
  const [isApplyingImprovePreview, setIsApplyingImprovePreview] = useState(false);
  const [improvePreviewError, setImprovePreviewError] = useState('');
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [isLoadingKnowledgeBases, setIsLoadingKnowledgeBases] = useState(true);
  const [mcpServers, setMcpServers] = useState([]);
  const [isLoadingMcpServers, setIsLoadingMcpServers] = useState(true);
  const [initialValuesApplied, setInitialValuesApplied] = useState(false);

  const displayError = validationError || submitError;
  const toneStackKey = buildTextFieldStackKey(
    'agent',
    persistedAgentId || 'draft',
    'tone_of_voice'
  );
  const defaultKnowledgeBaseId =
    wizardKnowledgeBase?.status === 'error' ? null : wizardKnowledgeBase?.id || null;

  const availableKnowledgeBases = useMemo(
    () => filterSelectableKnowledgeBases(mergeWizardKnowledgeBase(knowledgeBases, wizardKnowledgeBase)),
    [knowledgeBases, wizardKnowledgeBase]
  );

  useEffect(() => {
    if (!initialValues || initialValuesApplied) {
      return;
    }

    setAgentName(initialValues.agentName || '');
    setToneOfVoice(initialValues.toneOfVoice || '');
    setSubAgents(initialValues.subAgents || []);
    setLlmModelLineId(initialValues.llmModelLineId || '');
    setLlmEffortId(initialValues.llmEffortId || '');
    setSessionTimeoutHours(initialValues.sessionTimeoutHours || '4');
    setSessionClosePolicy(
      initialValues.sessionClosePolicy || DEFAULT_SESSION_CLOSE_POLICY
    );
    setEscalationCooldownHours(
      initialValues.escalationCooldownHours ?? DEFAULT_ESCALATION_COOLDOWN_HOURS
    );
    setKnowledgeRelevanceMode(initialValues.knowledgeRelevanceMode || 'high');
    setProcessingFeedback(
      initialValues.processingFeedback || createDefaultProcessingFeedbackForm()
    );
    setRequireRegisteredContact(initialValues.requireRegisteredContact || false);
    setShareCrossAgentContext(initialValues.shareCrossAgentContext || false);
    setBehaviorExecutionSelective(Boolean(initialValues.behaviorExecutionSelective));
    setKnowledgeFileButtonEnabled(initialValues.knowledgeFileButtonEnabled !== false);
    setInteractiveOptionsEnabled(Boolean(initialValues.interactiveOptionsEnabled));
    setInteractiveListButtonLabel(initialValues.interactiveListButtonLabel || '');
    setVisualOutputEnabled(Boolean(initialValues.visualOutputEnabled));
    setVisualOutputFormats(initialValues.visualOutputFormats || ['png', 'jpg', 'pdf']);
    setVisualOutputStyleInstructions(initialValues.visualOutputStyleInstructions || '');
    setVisualOutputAssets(initialValues.visualOutputAssets || {});
    setMessagePolishMode(initialValues.messagePolishMode || 'standard');
    setResponseDepth(initialValues.responseDepth || 'chat');
    setMessageFormat(initialValues.messageFormat || 'simple');
    setLongRunningAnalysis(
      initialValues.longRunningAnalysis || createDefaultLongRunningAnalysisForm()
    );
    setInitialValuesApplied(true);
  }, [initialValues, initialValuesApplied]);

  useEffect(() => {
    let cancelled = false;

    listLlmProfiles()
      .then((catalog) => {
        if (cancelled) return;
        const lines = catalog.model_lines || [];
        const efforts = catalog.efforts || [];
        setLlmModelLines(lines);
        setLlmEfforts(efforts);
        setLlmCatalogError('');

        if (!isEditMode || !initialValues?.llmModelLineId) {
          const defaultLineId = lines.some((line) => line.id === catalog.default_model_line_id)
            ? catalog.default_model_line_id
            : lines[0]?.id || '';
          setLlmModelLineId(defaultLineId);
        }

        if (!isEditMode || !initialValues?.llmEffortId) {
          const defaultEffortId = efforts.some((effort) => effort.id === catalog.default_effort_id)
            ? catalog.default_effort_id
            : efforts[0]?.id || '';
          setLlmEffortId(defaultEffortId);
        }

        if (!lines.length) {
          setLlmCatalogError(
            'Nenhum modelo LLM disponível. Peça a um administrador para configurar o Catálogo LLM.'
          );
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setLlmModelLines([]);
          setLlmEfforts([]);
          setLlmCatalogError(
            error.response?.data?.detail ||
              'Não foi possível carregar os modelos LLM. Tente novamente.'
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingLlmCatalog(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isEditMode, initialValues?.llmModelLineId, initialValues?.llmEffortId]);

  useEffect(() => {
    let cancelled = false;

    listKnowledgeBases({ page: 1, perPage: 100 })
      .then((response) => {
        if (!cancelled) {
          setKnowledgeBases(response.data || []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setKnowledgeBases([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingKnowledgeBases(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    listMcpServers({ page: 1, perPage: 100, isActive: true })
      .then((response) => {
        if (!cancelled) {
          setMcpServers(response.data || []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMcpServers([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingMcpServers(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const selectableIds = new Set(availableKnowledgeBases.map((kb) => kb.id));
    setSubAgents((current) => {
      const next = current.map((item) => ({
        ...item,
        knowledgeBaseIds: (item.knowledgeBaseIds || []).filter((id) => selectableIds.has(id)),
      }));
      const changed = next.some(
        (item, index) =>
          item.knowledgeBaseIds.length !== (current[index].knowledgeBaseIds || []).length
      );
      return changed ? next : current;
    });
  }, [availableKnowledgeBases]);

  const handleSubAgentChange = (id, patch) => {
    setSubAgents((current) =>
      current.map((item) => (item.id === id ? { ...item, ...patch } : item))
    );
  };

  const resetImproveModal = () => {
    setImproveModalKind(null);
    setImproveModalPhase('form');
    setTriageQuestions([]);
    setPendingImproveConsiderations('');
    setPendingImproveSubAgent(null);
    setPendingImproveMcpBinding(null);
    setImproveModalError('');
  };

  const handleCloseImproveModal = () => {
    if (isImproveModalSubmitting) {
      return;
    }
    resetImproveModal();
  };

  const handleOpenImproveToneModal = () => {
    const draft = toneOfVoice.trim();
    if (!draft) {
      setToneImproveError('Escreva um rascunho antes de melhorar o texto.');
      return;
    }

    setToneImproveError('');
    setImproveModalError('');
    setImproveModalPhase('form');
    setTriageQuestions([]);
    setPendingImproveConsiderations('');
    setImproveModalKind('tone');
    setPendingImproveSubAgent(null);
  };

  const handleRequestImproveInstructions = (subAgent) => {
    setImproveModalError('');
    setImproveModalPhase('form');
    setTriageQuestions([]);
    setPendingImproveConsiderations('');
    setImproveModalKind('instructions');
    setPendingImproveSubAgent(subAgent);
    setPendingImproveMcpBinding(null);
  };

  const resolveMcpServerMeta = (mcpServerId) => {
    const server = mcpServers.find((item) => item.id === mcpServerId);
    return {
      label: server?.label || server?.slug || undefined,
      slug: server?.slug || undefined,
    };
  };

  const handleRequestImproveMcpInstructions = (subAgent, binding) => {
    setImproveModalError('');
    setImproveModalPhase('form');
    setTriageQuestions([]);
    setPendingImproveConsiderations('');
    setImproveModalKind('mcp_usage_instructions');
    setPendingImproveSubAgent(null);
    setPendingImproveMcpBinding({ subAgent, binding });
  };

  const runImproveWithConsiderations = async (userConsiderations) => {
    setImproveModalPhase('improving');
    setIsImproveModalSubmitting(true);

    try {
      if (improveModalKind === 'tone') {
        setIsImprovingTone(true);
        const draft = toneOfVoice.trim();
        const { text } = await improveToneOfVoice({
          text: draft,
          agentName: agentName.trim() || undefined,
          llmModelLineId,
          llmEffortId,
          userConsiderations: userConsiderations || undefined,
        });
        setImprovePreview({
          kind: 'tone',
          previousText: draft,
          improvedText: text,
          resourceType: 'agent',
          resourceId: persistedAgentId,
          fieldName: 'tone_of_voice',
          userConsiderations: userConsiderations || '',
        });
      } else if (improveModalKind === 'instructions' && pendingImproveSubAgent) {
        setImprovingSubAgentId(pendingImproveSubAgent.id);
        const subAgent = pendingImproveSubAgent;
        const draft = subAgent.instructions.trim();
        const { text } = await improveSubAgentInstructions({
          text: draft,
          subAgentType: subAgent.type,
          agentName: agentName?.trim() || undefined,
          executionPhase: getExecutionPhase(subAgent),
          llmModelLineId: subAgent.inheritLlmProfile
            ? llmModelLineId
            : subAgent.llmModelLineId || llmModelLineId,
          llmEffortId: subAgent.inheritLlmProfile
            ? llmEffortId
            : subAgent.llmEffortId || llmEffortId,
          userConsiderations: userConsiderations || undefined,
        });
        setImprovePreview({
          kind: 'instructions',
          previousText: draft,
          improvedText: text,
          resourceType: 'sub_agent',
          resourceId: subAgent.id,
          fieldName: 'instructions',
          subAgentId: subAgent.id,
          userConsiderations: userConsiderations || '',
        });
      } else if (
        improveModalKind === 'mcp_usage_instructions' &&
        pendingImproveMcpBinding
      ) {
        const { subAgent, binding } = pendingImproveMcpBinding;
        setImprovingMcpBindingId(binding.id);
        const draft = binding.instructions.trim();
        const { label: mcpServerLabel, slug: mcpServerSlug } = resolveMcpServerMeta(
          binding.mcpServerId
        );
        const { text } = await improveMcpUsageInstructions({
          text: draft,
          mcpServerId: binding.mcpServerId,
          mcpServerLabel,
          mcpServerSlug,
          subAgentType: subAgent.type,
          subAgentInstructions: subAgent.instructions?.trim() || undefined,
          agentName: agentName?.trim() || undefined,
          executionPhase: getExecutionPhase(subAgent),
          llmModelLineId: subAgent.inheritLlmProfile
            ? llmModelLineId
            : subAgent.llmModelLineId || llmModelLineId,
          llmEffortId: subAgent.inheritLlmProfile
            ? llmEffortId
            : subAgent.llmEffortId || llmEffortId,
          userConsiderations: userConsiderations || undefined,
        });
        setImprovePreview({
          kind: 'mcp_usage_instructions',
          previousText: draft,
          improvedText: text,
          resourceType: 'sub_agent',
          resourceId: subAgent.id,
          fieldName: `mcp:${binding.id}:instructions`,
          subAgentId: subAgent.id,
          bindingId: binding.id,
          userConsiderations: userConsiderations || '',
        });
      }

      resetImproveModal();
    } catch (error) {
      const message =
        error.response?.data?.detail ||
        'Não foi possível melhorar o texto agora. Tente novamente.';
      setImproveModalError(typeof message === 'string' ? message : 'Erro ao melhorar o texto.');
      setImproveModalPhase('form');
    } finally {
      setIsImproveModalSubmitting(false);
      setIsImprovingTone(false);
      setImprovingSubAgentId(null);
      setImprovingMcpBindingId(null);
    }
  };

  const handleConfirmImproveModal = async (userConsiderations) => {
    setImproveModalError('');
    setPendingImproveConsiderations(userConsiderations || '');
    setImproveModalPhase('analyzing');
    setIsImproveModalSubmitting(true);

    try {
      let triage = { ready: true, questions: [] };

      try {
        if (improveModalKind === 'tone') {
          triage = await triageToneOfVoice({
            text: toneOfVoice.trim(),
            agentName: agentName.trim() || undefined,
            llmModelLineId,
            llmEffortId,
            userConsiderations: userConsiderations || undefined,
          });
        } else if (improveModalKind === 'instructions' && pendingImproveSubAgent) {
          const subAgent = pendingImproveSubAgent;
          triage = await triageSubAgentInstructions({
            text: subAgent.instructions.trim(),
            subAgentType: subAgent.type,
            agentName: agentName?.trim() || undefined,
            executionPhase: getExecutionPhase(subAgent),
            llmModelLineId: subAgent.inheritLlmProfile
              ? llmModelLineId
              : subAgent.llmModelLineId || llmModelLineId,
            llmEffortId: subAgent.inheritLlmProfile
              ? llmEffortId
              : subAgent.llmEffortId || llmEffortId,
            userConsiderations: userConsiderations || undefined,
          });
        } else if (
          improveModalKind === 'mcp_usage_instructions' &&
          pendingImproveMcpBinding
        ) {
          const { subAgent, binding } = pendingImproveMcpBinding;
          const { label: mcpServerLabel, slug: mcpServerSlug } = resolveMcpServerMeta(
            binding.mcpServerId
          );
          triage = await triageMcpUsageInstructions({
            text: binding.instructions.trim(),
            mcpServerId: binding.mcpServerId,
            mcpServerLabel,
            mcpServerSlug,
            subAgentType: subAgent.type,
            subAgentInstructions: subAgent.instructions?.trim() || undefined,
            agentName: agentName?.trim() || undefined,
            executionPhase: getExecutionPhase(subAgent),
            llmModelLineId: subAgent.inheritLlmProfile
              ? llmModelLineId
              : subAgent.llmModelLineId || llmModelLineId,
            llmEffortId: subAgent.inheritLlmProfile
              ? llmEffortId
              : subAgent.llmEffortId || llmEffortId,
            userConsiderations: userConsiderations || undefined,
          });
        }
      } catch (_triageError) {
        // Keep going, but prefer asking when the draft looks too thin.
        const draftForFallback =
          improveModalKind === 'tone'
            ? toneOfVoice.trim()
            : improveModalKind === 'instructions' && pendingImproveSubAgent
              ? pendingImproveSubAgent.instructions.trim()
              : improveModalKind === 'mcp_usage_instructions' && pendingImproveMcpBinding
                ? pendingImproveMcpBinding.binding.instructions.trim()
                : '';
        const thin =
          draftForFallback.length <= 160 || draftForFallback.trim().split(/\s+/).length <= 28;
        triage = thin
          ? {
              ready: false,
              questions: [
                {
                  id: 'objetivo',
                  prompt: 'Qual objetivo concreto esse texto deve deixar claro?',
                  why: 'O rascunho está genérico demais para melhorar sem inventar.',
                },
                {
                  id: 'limites',
                  prompt: 'O que deve ser evitado ou nunca feito?',
                  why: 'Limites ajudam a tornar a instrução específica.',
                },
                {
                  id: 'quando',
                  prompt: 'Em que situações isso se aplica?',
                  why: 'Sem gatilhos o resultado fica genérico.',
                },
              ],
            }
          : { ready: true, questions: [] };
      }

      const questions = Array.isArray(triage?.questions) ? triage.questions : [];
      if (triage?.ready === false && questions.length > 0) {
        setTriageQuestions(questions);
        setImproveModalPhase('questions');
        setIsImproveModalSubmitting(false);
        return;
      }

      await runImproveWithConsiderations(userConsiderations || '');
    } catch (error) {
      const message =
        error.response?.data?.detail ||
        'Não foi possível melhorar o texto agora. Tente novamente.';
      setImproveModalError(typeof message === 'string' ? message : 'Erro ao melhorar o texto.');
      setImproveModalPhase('form');
      setIsImproveModalSubmitting(false);
    }
  };

  const handleContinueImproveWithAnswers = async ({ considerations, answers }) => {
    setImproveModalError('');
    const merged = mergeImproveConsiderations(
      considerations || pendingImproveConsiderations,
      answers
    );
    await runImproveWithConsiderations(merged || '');
  };

  const handleSkipImproveQuestions = async ({ considerations }) => {
    setImproveModalError('');
    await runImproveWithConsiderations(
      considerations || pendingImproveConsiderations || ''
    );
  };

  const handleBackToImproveForm = () => {
    if (isImproveModalSubmitting) {
      return;
    }
    setImproveModalPhase('form');
    setTriageQuestions([]);
    setImproveModalError('');
  };

  const handleDiscardImprovePreview = () => {
    if (isApplyingImprovePreview) {
      return;
    }
    setImprovePreview(null);
    setImprovePreviewError('');
  };

  const handleBackToImproveConsiderations = () => {
    if (isApplyingImprovePreview || !improvePreview) {
      return;
    }
    setImprovePreview(null);
    setImprovePreviewError('');
    setImproveModalPhase('form');
    setTriageQuestions([]);
    setPendingImproveConsiderations(improvePreview.userConsiderations || '');
    setImproveModalKind(improvePreview.kind);
    if (improvePreview.kind === 'instructions' && improvePreview.subAgentId) {
      const subAgent = subAgents.find((item) => item.id === improvePreview.subAgentId);
      if (subAgent) {
        setPendingImproveSubAgent(subAgent);
      }
    } else if (
      improvePreview.kind === 'mcp_usage_instructions' &&
      improvePreview.subAgentId &&
      improvePreview.bindingId
    ) {
      const subAgent = subAgents.find((item) => item.id === improvePreview.subAgentId);
      const binding = subAgent?.mcpBindings?.find(
        (item) => item.id === improvePreview.bindingId
      );
      if (subAgent && binding) {
        setPendingImproveMcpBinding({ subAgent, binding });
      }
    }
  };

  const handleApplyImprovePreview = async () => {
    if (!improvePreview) {
      return;
    }

    setIsApplyingImprovePreview(true);
    setImprovePreviewError('');

    const {
      kind,
      previousText,
      improvedText,
      resourceType,
      resourceId,
      fieldName,
      subAgentId,
      bindingId,
      userConsiderations,
    } = improvePreview;

    const stackKey = buildTextFieldStackKey(
      resourceType,
      resourceId || 'draft',
      fieldName
    );

    try {
      textVersionStack.push(stackKey, previousText);

      if (isPersistedUuid(resourceId) && kind !== 'mcp_usage_instructions') {
        await createTextFieldVersion({
          resourceType,
          resourceId,
          fieldName,
          content: previousText,
          source: 'ai_improve',
          metadata: {
            user_considerations: userConsiderations || undefined,
          },
        });
      }

      if (kind === 'tone') {
        setToneOfVoice(improvedText);
      } else if (kind === 'instructions' && subAgentId) {
        handleSubAgentChange(subAgentId, { instructions: improvedText });
      } else if (kind === 'mcp_usage_instructions' && subAgentId && bindingId) {
        const subAgent = subAgents.find((item) => item.id === subAgentId);
        if (subAgent) {
          const updatedBindings = (subAgent.mcpBindings || []).map((binding) =>
            binding.id === bindingId
              ? { ...binding, instructions: improvedText }
              : binding
          );
          handleSubAgentChange(subAgentId, { mcpBindings: updatedBindings });
        }
      }

      setImprovePreview(null);
    } catch (error) {
      const message =
        error.response?.data?.detail ||
        'Não foi possível aplicar a melhoria agora. Tente novamente.';
      setImprovePreviewError(typeof message === 'string' ? message : 'Erro ao aplicar melhoria.');
    } finally {
      setIsApplyingImprovePreview(false);
    }
  };

  const handleUndoTone = () => {
    const previous = textVersionStack.undo(toneStackKey);
    if (previous !== null) {
      setToneOfVoice(previous);
    }
  };

  const handleAddSubAgent = () => {
    setSubAgents((current) => [...current, createEmptySubAgent(defaultKnowledgeBaseId)]);
  };

  const handleRemoveSubAgent = (id) => {
    setSubAgents((current) => current.filter((item) => item.id !== id));
  };

  const handleSubmit = () => {
    setValidationError('');

    if (!agentName.trim()) {
      setValidationError('Informe o nome do agente.');
      return;
    }
    if (!toneOfVoice.trim()) {
      setValidationError('Informe o tom de voz do agente.');
      return;
    }
    if (!llmModelLineId || !llmEffortId) {
      setValidationError('Selecione o modelo e o esforço do agente.');
      return;
    }
    const escalationCooldownConfiguration =
      buildEscalationCooldownConfiguration(escalationCooldownHours);
    if (
      !Object.prototype.hasOwnProperty.call(
        escalationCooldownConfiguration,
        'escalation_cooldown_hours'
      )
    ) {
      setValidationError('Informe a pausa após transbordo em horas inteiras, a partir de 0.');
      return;
    }

    const incompleteSubAgent = subAgents.find((item) => isSubAgentIncomplete(item));
    if (incompleteSubAgent) {
      const missingMcpInstructions = (incompleteSubAgent.mcpBindings || []).some(
        (binding) => binding.mcpServerId && !binding.instructions?.trim()
      );
      const webhookError = getCompletionWebhookConfigError(incompleteSubAgent);
      setValidationError(
        webhookError
          || (missingMcpInstructions
            ? 'Preencha as instruções de uso de todos os MCPs vinculados nos comportamentos.'
            : 'Preencha as instruções de todos os comportamentos adicionados.')
      );
      return;
    }

    const configurations = {};
    const timeout = parseInt(sessionTimeoutHours, 10);
    if (!Number.isNaN(timeout) && timeout > 0) {
      configurations.session_timeout_hours = timeout;
    }
    configurations.session_close_policy =
      sessionClosePolicy || DEFAULT_SESSION_CLOSE_POLICY;
    Object.assign(configurations, escalationCooldownConfiguration);
    if (knowledgeRelevanceMode) {
      configurations.knowledge_relevance_mode = knowledgeRelevanceMode;
    }
    Object.assign(configurations, buildProcessingFeedbackConfiguration(processingFeedback));
    Object.assign(
      configurations,
      buildBehaviorExecutionConfiguration(behaviorExecutionSelective)
    );
    Object.assign(configurations, buildMessagePolishConfiguration(messagePolishMode));
    Object.assign(configurations, buildResponseDepthConfiguration(responseDepth));
    Object.assign(configurations, buildMessageFormatConfiguration(messageFormat));
    Object.assign(
      configurations,
      buildLongRunningAnalysisConfiguration(longRunningAnalysis)
    );
    configurations.require_registered_contact = requireRegisteredContact;
    configurations.share_cross_agent_context = shareCrossAgentContext;
    configurations.knowledge_file_button_enabled = knowledgeFileButtonEnabled;
    configurations.interactive_options_enabled = interactiveOptionsEnabled;
    const listButtonLabel = String(interactiveListButtonLabel || '').trim().slice(0, 20);
    if (interactiveOptionsEnabled) {
      configurations.interactive_list_button_label =
        listButtonLabel || DEFAULT_INTERACTIVE_LIST_BUTTON_LABEL;
    } else if (listButtonLabel) {
      configurations.interactive_list_button_label = listButtonLabel;
    }
    configurations.visual_output_enabled = visualOutputEnabled;
    if (visualOutputEnabled) {
      configurations.visual_output_formats = visualOutputFormats.length
        ? visualOutputFormats
        : ['png', 'jpg', 'pdf'];
      const styleGuide = visualOutputStyleInstructions.trim();
      if (styleGuide) {
        configurations.visual_output_style_instructions = styleGuide;
      }
    }

    onSubmit({
      agentName: agentName.trim(),
      toneOfVoice: toneOfVoice.trim(),
      llmModelLineId,
      llmEffortId,
      configurations,
      subAgents: subAgents.map((item) => {
        const selectableIds = new Set(availableKnowledgeBases.map((kb) => kb.id));
        return {
          ...item,
          instructions: item.instructions.trim(),
          knowledgeBaseIds: (item.knowledgeBaseIds || []).filter((id) => selectableIds.has(id)),
        };
      }),
    });
  };

  if (isLoadingInitial) {
    return (
      <div className="surface-card px-4 py-12 text-center">
        <p className="text-content-muted text-sm">Carregando agente...</p>
      </div>
    );
  }

  return (
    <WizardShell
      title={isEditMode ? 'Editar agente' : 'Criar seu agente'}
      subtitle={
        isEditMode
          ? 'Atualize nome, tom de voz, perfil LLM e comportamentos do agente.'
          : hasKnowledge
            ? 'Configure o agente e escolha quais bases cada comportamento deve usar.'
            : 'Quase pronto — nome, tom de voz e comportamentos opcionais com bases de conhecimento.'
      }
      step={stepNumber}
      totalSteps={totalSteps}
      showSteps={!isEditMode}
    >
      <div className="space-y-6">
        <FormSection
          title="Identidade do agente"
          description="Nome e tom de voz definem a personalidade do assistente."
        >
          <Input
            id="agent_name"
            label="Nome do agente"
            value={agentName}
            onChange={(e) => setAgentName(e.target.value)}
            placeholder="Assistente de Suporte"
            required
            autoFocus
          />

          <Textarea
            id="tone_of_voice"
            label="Tom de voz"
            value={toneOfVoice}
            onChange={(e) => {
              setToneOfVoice(e.target.value);
              if (toneImproveError) {
                setToneImproveError('');
              }
            }}
            placeholder="Fale de forma acolhedora e direta. Responda sempre em português brasileiro."
            hint="Descreva como o agente deve se comunicar. Use o ícone de IA no canto do campo para refinar o texto."
            required
            rows={5}
            action={{
              iconOnly: true,
              ariaLabel: 'Melhorar tom de voz com IA',
              loading: isImprovingTone || isImproveModalSubmitting,
              disabled: isSubmitting || isImproveModalSubmitting || isApplyingImprovePreview,
              onClick: handleOpenImproveToneModal,
              error: toneImproveError,
            }}
          />
          <TextFieldActions
            stackKey={toneStackKey}
            canUndo={textVersionStack.canUndo(toneStackKey)}
            onUndo={handleUndoTone}
            resourceType="agent"
            resourceId={persistedAgentId}
            fieldName="tone_of_voice"
            onRestoreFromHistory={setToneOfVoice}
            disabled={isSubmitting || isImproveModalSubmitting || isApplyingImprovePreview}
          />
        </FormSection>

        <FormSection
          title="Comportamentos"
          description="Opcional. Adicione regras extras como boas-vindas, conhecimento, segurança ou escalonamento."
          bordered
        >
          <div className="rounded-lg border border-surface-border bg-surface-muted p-4 space-y-2">
            <label className="flex items-start gap-3 text-sm text-content-secondary">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-forge-600 focus:ring-forge-500"
                checked={behaviorExecutionSelective}
                onChange={(event) => setBehaviorExecutionSelective(event.target.checked)}
              />
              <span>
                <span className="block font-medium text-content-primary">
                  Executar habilidades conforme o contexto
                </span>
                <span className="mt-1 block text-xs text-content-muted">
                  O agente usa a descrição de cada habilidade para escolher quais executar
                  antes da resposta. Habilidades depois da resposta e outros comportamentos
                  não são afetados.
                </span>
              </span>
            </label>
          </div>

          <SubAgentList
            subAgents={subAgents}
            onChange={handleSubAgentChange}
            onAdd={handleAddSubAgent}
            onRemove={handleRemoveSubAgent}
            onRequestImproveInstructions={handleRequestImproveInstructions}
            onRequestImproveMcpInstructions={handleRequestImproveMcpInstructions}
            isSubmitting={isSubmitting || isLoadingKnowledgeBases || isLoadingMcpServers || isLoadingLlmCatalog}
            isImproveModalSubmitting={isImproveModalSubmitting}
            isApplyingImprovePreview={isApplyingImprovePreview}
            improvingSubAgentId={improvingSubAgentId}
            improvingMcpBindingId={improvingMcpBindingId}
            textVersionStack={textVersionStack}
            knowledgeBases={availableKnowledgeBases}
            defaultKnowledgeBaseId={defaultKnowledgeBaseId}
            llmModelLines={llmModelLines}
            llmEfforts={llmEfforts}
            mcpServers={mcpServers}
            isEditMode={isEditMode}
          />
        </FormSection>

        <div className="border-t border-surface-border pt-6">
          <AgentAdvancedSettings
            llmModelLines={llmModelLines}
            llmEfforts={llmEfforts}
            llmModelLineId={llmModelLineId}
            setLlmModelLineId={setLlmModelLineId}
            llmEffortId={llmEffortId}
            setLlmEffortId={setLlmEffortId}
            isLoadingLlmCatalog={isLoadingLlmCatalog}
            llmCatalogError={llmCatalogError}
            sessionTimeoutHours={sessionTimeoutHours}
            setSessionTimeoutHours={setSessionTimeoutHours}
            sessionClosePolicy={sessionClosePolicy}
            setSessionClosePolicy={setSessionClosePolicy}
            escalationCooldownHours={escalationCooldownHours}
            setEscalationCooldownHours={setEscalationCooldownHours}
            knowledgeRelevanceMode={knowledgeRelevanceMode}
            setKnowledgeRelevanceMode={setKnowledgeRelevanceMode}
            knowledgeFileButtonEnabled={knowledgeFileButtonEnabled}
            setKnowledgeFileButtonEnabled={setKnowledgeFileButtonEnabled}
            interactiveOptionsEnabled={interactiveOptionsEnabled}
            setInteractiveOptionsEnabled={setInteractiveOptionsEnabled}
            interactiveListButtonLabel={interactiveListButtonLabel}
            setInteractiveListButtonLabel={setInteractiveListButtonLabel}
            visualOutputEnabled={visualOutputEnabled}
            setVisualOutputEnabled={setVisualOutputEnabled}
            visualOutputFormats={visualOutputFormats}
            setVisualOutputFormats={setVisualOutputFormats}
            visualOutputStyleInstructions={visualOutputStyleInstructions}
            setVisualOutputStyleInstructions={setVisualOutputStyleInstructions}
            visualOutputAssets={visualOutputAssets}
            setVisualOutputAssets={setVisualOutputAssets}
            agentId={persistedAgentId}
            requireRegisteredContact={requireRegisteredContact}
            setRequireRegisteredContact={setRequireRegisteredContact}
            shareCrossAgentContext={shareCrossAgentContext}
            setShareCrossAgentContext={setShareCrossAgentContext}
            processingFeedback={processingFeedback}
            onProcessingFeedbackChange={(patch) =>
              setProcessingFeedback((current) => ({ ...current, ...patch }))
            }
            messagePolishMode={messagePolishMode}
            setMessagePolishMode={setMessagePolishMode}
            responseDepth={responseDepth}
            setResponseDepth={setResponseDepth}
            messageFormat={messageFormat}
            setMessageFormat={setMessageFormat}
            longRunningAnalysis={longRunningAnalysis}
            setLongRunningAnalysis={setLongRunningAnalysis}
          />
        </div>

        {displayError && (
          <p className="text-sm text-red-600">{displayError}</p>
        )}
      </div>

      <StepActions
        onBack={onBack}
        onPrimary={handleSubmit}
        primaryLabel={isEditMode ? 'Salvar alterações' : 'Criar agente'}
        backLabel={isEditMode ? 'Cancelar' : 'Voltar'}
        isSubmitting={isSubmitting}
      />

      <ImproveTextModal
        open={Boolean(improveModalKind)}
        kind={improveModalKind || 'tone'}
        phase={improveModalPhase}
        questions={triageQuestions}
        initialConsiderations={pendingImproveConsiderations}
        isSubmitting={isImproveModalSubmitting}
        error={improveModalError}
        onClose={handleCloseImproveModal}
        onSubmit={handleConfirmImproveModal}
        onContinueWithAnswers={handleContinueImproveWithAnswers}
        onSkipQuestions={handleSkipImproveQuestions}
        onBackToForm={handleBackToImproveForm}
      />

      <ImprovePreviewModal
        open={Boolean(improvePreview)}
        kind={improvePreview?.kind || 'tone'}
        previousText={improvePreview?.previousText || ''}
        improvedText={improvePreview?.improvedText || ''}
        isApplying={isApplyingImprovePreview}
        error={improvePreviewError}
        onDiscard={handleDiscardImprovePreview}
        onBackToConsiderations={handleBackToImproveConsiderations}
        onApply={handleApplyImprovePreview}
      />
    </WizardShell>
  );
}

export default AgentSetup;
